"use client";

import {
  formatMileage,
  formatPercentage,
  formatPriceWithFallback,
} from "@/app/components/offers/utils/formatters";
import { getOfferTypeClassificationType } from "@/app/utils/helpers/backend/offers/get-offer-type-classification";
import { getTotalMonthlyLeasingRate } from "@/app/utils/helpers/backend/offers/get-total-monthly-leasing-rate";
import type { OfferFormDataT } from "@/app/types/offers";

type VehiclePricingDetailsPropsT = {
  pricing: OfferFormDataT["pricing"];
  annualMileage: number | null;
  offerTypes?: OfferFormDataT["offer_types"];
  vehicleCondition?: OfferFormDataT["vehicle_condition"];
  financingFootnoteMarker?: "¹" | null;
};

/**
 * Format duration in months
 */
const formatDuration = (duration: number | null | undefined): string => {
  if (!duration) return "-";
  return `${duration} Monate`;
};

/**
 * Get VAT label text based on offer type, vehicle condition and VAT display option
 *
 * - "zzgl. MwSt." → business (gewerbe) + new vehicle condition for barkauf WITH NETTO PRICE
 * - "inkl. MwSt." → private (privat) or when brutto price is used
 * - IMPORTANT: NEVER show "zzgl. MwSt." with brutto price!
 */
const getVatLabel = (
  offerTypes?: OfferFormDataT["offer_types"],
  vehicleCondition?: OfferFormDataT["vehicle_condition"],
  pricingType?: OfferFormDataT["pricing"] | null,
  isUsingNettoPrice?: boolean
): string => {
  const pricingTypeValue = pricingType?.type;
  const classification = getOfferTypeClassificationType(offerTypes);
  const isBusiness = classification === "business";
  const isPrivate = classification === "private";
  const isNew = vehicleCondition === "new";

  // For barkauf: only show "zzgl. MwSt." if netto price is used
  if (
    pricingTypeValue === "barkauf" &&
    isBusiness &&
    isNew &&
    isUsingNettoPrice
  ) {
    return "zzgl. MwSt.";
  }

  // For brutto prices: always show "inkl. MwSt." (NEVER "zzgl. MwSt." with brutto)
  if (pricingTypeValue === "barkauf" && !isUsingNettoPrice) {
    if (isPrivate) {
      return "inkl. MwSt.";
    }
    // For business with brutto: show "inkl. MwSt." (not "zzgl. MwSt.")
    return "inkl. MwSt.";
  }

  // For leasing and finanzierung: use normal business/private logic
  if (isBusiness) {
    return "zzgl. MwSt.";
  }

  if (isPrivate) {
    return "inkl. MwSt.";
  }

  // Default fallback for all other cases
  return "";
};

/**
 * VehiclePricingDetails
 *
 * Renders pricing details based on the pricing type (leasing, finanzierung, barkauf).
 * Displays relevant fields for each pricing type.
 */
export default function VehiclePricingDetails({
  pricing,
  annualMileage,
  offerTypes,
  vehicleCondition,
  financingFootnoteMarker = null,
}: VehiclePricingDetailsPropsT) {
  if (!pricing || !pricing.type) {
    return null;
  }

  switch (pricing.type) {
    case "leasing": {
      const leasing = pricing.leasing;
      if (!leasing) return null;

      const totalMonthlyRate = getTotalMonthlyLeasingRate(pricing);

      return (
        <div id="details" className="flex flex-col gap-1 pt-4">
          <div className="flex flex-row justify-between gap-2">
            <div>Fahrleistung:</div>
            <div className="font-semibold">{formatMileage(annualMileage)}</div>
          </div>
          <div className="flex flex-row justify-between gap-2">
            <div>Laufzeit:</div>
            <div className="font-semibold">
              {formatDuration(leasing.vertragsdauer)}
            </div>
          </div>
          <div className="flex flex-row justify-between gap-2">
            <div>Überführungskosten:</div>
            <div className="font-semibold">
              {formatPriceWithFallback(pricing.ueberfuehrungskosten)}
            </div>
          </div>
          <div className="flex flex-row justify-between gap-2">
            <div>Anschaffungspreis:</div>
            <div className="font-semibold">
              {formatPriceWithFallback(leasing.anschaffungspreis)}
            </div>
          </div>
          {leasing.sollzinsProJahr != null &&
            leasing.sollzinsProJahr !== undefined && (
              <div className="flex flex-row justify-between gap-2">
                <div>Sollzins:</div>
                <div className="font-semibold">
                  {formatPercentage(leasing.sollzinsProJahr)}
                </div>
              </div>
            )}
          {leasing.effektiverJahreszins != null &&
            leasing.effektiverJahreszins !== undefined && (
              <div className="flex flex-row justify-between gap-2">
                <div>Effektivzins:</div>
                <div className="font-semibold">
                  {formatPercentage(leasing.effektiverJahreszins)}
                </div>
              </div>
            )}
          <div className="flex flex-row justify-between gap-2">
            <div>Sonderzahlung:</div>
            <div className="font-semibold">
              {formatPriceWithFallback(leasing.sonderzahlung)}
            </div>
          </div>
          <div className="flex flex-col items-start gap-0.5 pt-2">
            <div className="text-muted-foreground text-sm">
              mtl. Leasingrate
            </div>
            <div className="text-xl font-bold">
              {formatPriceWithFallback(totalMonthlyRate ?? 0)}
              {financingFootnoteMarker}
              <span className="text-secondary-foreground ml-2 text-xs font-normal">
                {getVatLabel(offerTypes, vehicleCondition, pricing, false)}
              </span>
            </div>
          </div>
        </div>
      );
    }

    case "finanzierung": {
      const finanzierung = pricing.finanzierung;
      if (!finanzierung) return null;

      return (
        <div id="details" className="pt-4">
          <div className="flex flex-row justify-between gap-2">
            <div>Laufzeit:</div>
            <div className="font-semibold">
              {formatDuration(finanzierung.vertragsdauer)}
            </div>
          </div>
          <div className="flex flex-row justify-between gap-2">
            <div>Überführungskosten:</div>
            <div className="font-semibold">
              {formatPriceWithFallback(pricing.ueberfuehrungskosten)}
            </div>
          </div>
          <div className="flex flex-row justify-between gap-2">
            <div>Anzahlung:</div>
            <div className="font-semibold">
              {formatPriceWithFallback(finanzierung.anzahlung)}
            </div>
          </div>

          {finanzierung.sollzinsProJahr != null &&
            finanzierung.sollzinsProJahr !== 0 && (
              <div className="flex flex-row justify-between gap-2">
                <div>Sollzins:</div>
                <div className="font-semibold">
                  {formatPercentage(finanzierung.sollzinsProJahr)}
                </div>
              </div>
            )}
          {finanzierung.effektiverJahreszins != null &&
            finanzierung.effektiverJahreszins !== 0 && (
              <div className="flex flex-row justify-between gap-2">
                <div>Effektivzins:</div>
                <div className="font-semibold">
                  {formatPercentage(finanzierung.effektiverJahreszins)}
                </div>
              </div>
            )}
          {finanzierung.abschlusszahlung && (
            <div className="flex flex-row justify-between gap-2">
              <div>Abschlusszahlung:</div>
              <div className="font-semibold">
                {formatPriceWithFallback(finanzierung.abschlusszahlung)}
              </div>
            </div>
          )}
          <div className="flex flex-col items-start gap-0.5 pt-2">
            <div className="text-muted-foreground text-sm">
              mtl. Finanzierungsrate
            </div>
            <div className="text-xl font-bold">
              {formatPriceWithFallback(finanzierung.mtlRate)}
              {financingFootnoteMarker}
              <span className="text-secondary-foreground ml-2 text-xs font-normal">
                {getVatLabel(offerTypes, vehicleCondition, pricing, false)}
              </span>
            </div>
          </div>
        </div>
      );
    }

    case "barkauf": {
      const barkauf = pricing.barkauf;
      if (!barkauf) return null;

      // Check if business offer type and new vehicle condition
      const isBusiness =
        getOfferTypeClassificationType(offerTypes) === "business";
      const isNew = vehicleCondition === "new";

      // For business + new: prioritize netto price with "zzgl. MwSt."
      // Only use netto if available, otherwise fallback to brutto (NEVER show "zzgl. MwSt." with brutto)
      const shouldUseNetto =
        isBusiness && isNew && barkauf.anschaffungspreisNetto != null;
      const displayPrice = shouldUseNetto
        ? barkauf.anschaffungspreisNetto
        : barkauf.anschaffungspreisBrutto;

      // Get VAT label - pass isUsingNettoPrice flag to ensure correct label
      const vatLabel = getVatLabel(
        offerTypes,
        vehicleCondition,
        pricing,
        shouldUseNetto
      );

      return (
        <div id="details" className="pt-4">
          <div className="flex flex-row justify-between gap-2">
            <div>Überführungskosten:</div>
            <div className="font-semibold">
              {formatPriceWithFallback(pricing.ueberfuehrungskosten)}
            </div>
          </div>
          {barkauf.anzahlung && (
            <div className="flex flex-row justify-between gap-2">
              <div>Anzahlung:</div>
              <div className="font-semibold">
                {formatPriceWithFallback(barkauf.anzahlung)}
              </div>
            </div>
          )}
          <div className="flex flex-col items-start gap-0.5 pt-2">
            <div className="text-muted-foreground text-sm">
              Anschaffungspreis
            </div>
            <div className="text-xl font-bold">
              {formatPriceWithFallback(displayPrice)}
              {financingFootnoteMarker}
              {vatLabel && (
                <span className="text-secondary-foreground ml-2 text-xs font-normal">
                  {vatLabel}
                </span>
              )}
            </div>
          </div>
        </div>
      );
    }

    default:
      return null;
  }
}
